import React from "react";
import { object, shape, string, oneOf, any } from "prop-types";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/smart-react/i18";
import { BackToTopButton } from "@insightfulscience/atomic-react/r2/BackToTopButton";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import { PostElementParagraph } from "@insightfulscience/atomic-react/PostElement";
import {
	GuidePostContainer,
	GuidePostMain,
	GuidePostNavItem,
	GuidePostNavItemLink,
	GuidePostSideBar,
} from "@insightfulscience/atomic-react/r2/GuidePostPartials";
import { scrollToHeading } from "@insightfulscience/atomic-react/utils/Guides";
import Breadcrumbs from "@insightfulscience/atomic-react/Breadcrumbs";
import BreadcrumbItem, {
	BreadcrumbItemLink,
} from "@insightfulscience/atomic-react/Breadcrumbs/Item";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import NextLink from "@insightfulscience/smart-react/NextLink";
import { generateHeadingId, sidebarNavigation } from "../../richTextTypes/default/utils";
import richTextTypes from "../../richTextTypes/default";
import { PUBLISHED } from "../../constants";

const ReleaseNotesPage = ({ post, pageProps }) => {
	const [t] = useLocale("updates");
	const { siteDomain } = pageProps;
	const {
		title,
		metaDescription = "",
		slug,
		releaseDate,
		appVersionNumber,
		publishStatus,
		body,
	} = post;
	const currentPageURL = `${siteDomain}/updates/${slug}`;
	const navigation = body ? sidebarNavigation(body) : [];
	const metaRobotsContent = publishStatus === "Live" ? "index, follow" : "noindex, follow";
	const wasReleasedMessage = (
		<PostElementParagraph italic suppressHydrationWarning>
			{t("PRODUCT_NAME")} {appVersionNumber} {t("UPDATES.WAS_RELEASED_ON")}{" "}
			{t("{{releaseDate, LONGDATE}}", { releaseDate })}.
		</PostElementParagraph>
	);

	const totalHeading2 = body.content.reduce((total, current) => {
		return total + (current.nodeType === "heading-2" ? 1 : 0);
	}, 0);
	const isSidebar = totalHeading2 > 0;

	return (
		<>
			<PageMeta title={title} description={metaDescription}>
				<PageMeta.OGP title={title} description={metaDescription} url={currentPageURL} />
				<PageMeta.Canonical href={currentPageURL} />
				<PageMeta.Robots content={metaRobotsContent} />
			</PageMeta>
			<Hero color="1">
				<Breadcrumbs>
					<BreadcrumbItem>
						<BreadcrumbItemLink href="/">Home</BreadcrumbItemLink>
					</BreadcrumbItem>
					<BreadcrumbItem>
						<NextLink Component={BreadcrumbItemLink} href="/updates">
							Updates
						</NextLink>
					</BreadcrumbItem>
					<BreadcrumbItem>{title}</BreadcrumbItem>
				</Breadcrumbs>
				<HeroTitle>{title}</HeroTitle>
			</Hero>

			<GuidePostContainer sidebar={isSidebar}>
				{navigation ? (
					<GuidePostSideBar>
						{navigation.map(item => {
							return (
								<GuidePostNavItem key={item}>
									<GuidePostNavItemLink
										href={`#${generateHeadingId(item)}`}
										onClick={e => scrollToHeading(e)}
									>
										{item}
									</GuidePostNavItemLink>
								</GuidePostNavItem>
							);
						})}
					</GuidePostSideBar>
				) : null}
				<GuidePostMain sidebar={isSidebar}>
					{publishStatus === "Live" && wasReleasedMessage}
					{body ? documentToReactComponents(body, richTextTypes) : null}
				</GuidePostMain>
			</GuidePostContainer>
			<BackToTopButton />
		</>
	);
};

ReleaseNotesPage.propTypes = {
	post: shape({
		title: string.isRequired,
		metaDescription: string,
		appReleaseNotes: object, // eslint-disable-line react/forbid-prop-types
		appVersionNumber: string,
		body: object, // eslint-disable-line react/forbid-prop-types
		publishStatus: oneOf(["Hidden", PUBLISHED]),
		releaseDate: string,
		slug: string,
		teaser: object, // eslint-disable-line react/forbid-prop-types
		macInstallerUrl: string,
		windowsInstallerUrl: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string,
	}),
};

export default ReleaseNotesPage;
